import { useState, useEffect, memo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UserActions } from 'BMapsCmds';
import { hasLogin } from 'BMapsSrc/server/session_request';
import { Link } from '@mui/material';
import { SidePanelContainer } from './info_display/SidePanel';
import { ErrorBoundary } from './PageWrapper';
import { ChatBot } from './ChatBot';
import ExternalLink from './common/ExternalLink';
import { MainTour } from './Tour';
import { TheManual } from './manual/TheManual';
import DialogContentSection from './common/DialogContentSection';

/**
 * Memoized because this subtree is expensive -- ChatBot is most of it -- and its props are
 * stable between side-panel events: SidePanel holds `pageInfoList` in state and re-clones from
 * it, so `handleClose`, `open`, `longLived` and `ChatBotProps` keep their identities across
 * re-renders that are not about this pane. The shallow compare therefore bails out in the
 * common case rather than never matching.
 *
 * Not for drag performance, whatever the fork's comment on it says: the drag commits once on
 * mouseup and moves only a ghost line in between, so it re-renders nothing per frame.
 */
export const HelpPanel = memo(HelpPanelImpl);

function HelpPanelImpl({
    handleClose, open, longLived, ChatBotProps, isStacked,
}) {
    const [loggedIn, setLoggedIn] = useState(false);
    useEffect(() => {
        hasLogin().then((newLoggedIn) => setLoggedIn(newLoggedIn));
    }, []);
    return (
        <SidePanelContainer
            open={open}
            handleClose={handleClose}
            longLived={longLived}
            // Set by SidePanel whenever more than one page in the column is open. Help declares
            // `stackable` in BMapsPage, so this is live as soon as a second stackable pane joins
            // the column. Note SidePanel supplies it, not SidePanelLayout.
            isStacked={isStacked}
            title="Help / Support"
            // No explicit `width`: the column's per-pane width wins, resolved from the
            // SidePanelLayout that BMapsPage mounts. The two are one unit -- without a
            // layout, paneId resolves to nothing and SidePanelContainer falls through to
            // DEFAULT_SIDEPANEL_WIDTH (18rem), narrower than the 20rem hard-coded here
            // previously, so removing the mount degrades silently.
            paneId="help"
            containerStyle={{
                background: '#DCF0E6',
                // set z index to 0 to override default
                // right side panel needs to be under the bfm_viewer so that tooltips are
                // displayed on top of it. this is not true for left side panel
                // which is on top of the bfm_viewer
                zIndex: 0,
            }}
        >
            <Typography component="div">
                <DialogContentSection
                    titleVariant="subtitle1"
                    collapsible
                    // Gibbs fork migration: review defaultExpand when applying SidePanelLayout
                    defaultExpand
                    disablePaddingTop
                    title="Help Information"
                    TitleProps={{ style: { padding: '0 .5rem' } }}
                >
                    <HelpSection title="In-App Help:" style={{ paddingTop: 0 }}>
                        <HelpButton onClick={() => MainTour.start()}>Launch Tour</HelpButton>
                        <HelpButton onClick={() => TheManual.ShowPage(TheManual.Pages.Main)}>
                            Open Manual
                        </HelpButton>
                        <HelpButton onClick={() => TheManual.ShowPage(TheManual.Pages.Cheatsheet)}>
                            Cheatsheet
                        </HelpButton>
                    </HelpSection>
                    <HelpSection title="Documentation:">
                        <HelpLink link="https://www.youtube.com/channel/UCoFsnMxpCs1NnTB-9JAb1fw?&ab_channel=ConiferPoint">Tutorial Videos</HelpLink>
                        <HelpLink link="https://www.boltzmannmaps.com/faqs">Frequently Asked Questions</HelpLink>
                        <HelpLink link="https://www.boltzmannmaps.com/documentation">Documentation Pages</HelpLink>
                    </HelpSection>
                    <HelpSection title="Contact Us:">
                        <HelpLink link="mailto:support@coniferpoint.com">support@coniferpoint.com</HelpLink>
                    </HelpSection>
                    { !!loggedIn && (
                        <HelpSection title="Connect:">
                            <HelpLink link="https://join.slack.com/t/bmaps-community/shared_invite/zt-2kuban85x-hbntjszQ7lUfTn2pj0j0Rg">
                                Join our Slack community
                            </HelpLink>
                        </HelpSection>
                    )}
                </DialogContentSection>
            </Typography>
            <ErrorBoundary surfaceName="Gibbs chat">
                <ChatBot
                    loggedIn={loggedIn}
                    {...ChatBotProps}
                />
            </ErrorBoundary>
        </SidePanelContainer>
    );
}

function HelpLink({ link, children }) {
    return (
        <Box>
            <ExternalLink
                link={link}
                style={{ color: 'var(--marketing-blue)' }}
                skipIcon
            >
                {children}
            </ExternalLink>
        </Box>
    );
}

function HelpSection({ title, style, children }) {
    const styleToUse = { paddingTop: '0.5rem', ...style };
    return (
        <Box style={styleToUse}>
            {title}
            {children}
        </Box>
    );
}

const HelpButton = ({ onClick, children }) => (
    <Box>
        <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
            component="button"
            onClick={onClick}
            sx={{ color: 'var(--marketing-blue)' }}
        >
            {children}
        </Link>
    </Box>
);

/**
 * @param {{
 *     ChatBotProps?: { initialMessages?: Array<{ role: string, content: string }> }
 * }} props
 */
export function openHelpMenu(props) {
    UserActions.OpenSidePanel('right', { pageId: 'help', props });
}

export function toggleHelpMenu() {
    UserActions.OpenSidePanel('right', { pageId: 'help', toggle: true });
}
